import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import {
  master,
  newPathFor,
  screenPath,
  sourceOfTruth,
} from '@/designs-dev/loft/projects';
import PhoneFrame from '@/designs-dev/device/PhoneFrame';
import { PhoneScreenContext } from '@/designs-dev/device/PhoneScreenContext';
import { type Viewport } from '@/designs-dev/device/viewport';
import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import SignUpFlow from '@/karehero-app/auth/SignUpFlow';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import PreSignUp from '@/karehero-app/auth/PreSignUp';
import {
  introScenes,
  introSceneById,
} from '@/karehero-app/auth/PreSignUp.scenes';
import MainTabs from '@/karehero-app/shell/MainTabs';
import ShortlistsV1Screen from '@/karehero-app/care/shortlists/ShortlistsV1Screen';
import {
  shortlistScenes,
  shortlistSceneById,
} from '@/karehero-app/care/shortlists/Shortlists.scenes';
import FinancialAssessment from '@/karehero-app/care/forms/FinancialAssessment';
import {
  financialAssessmentScenes,
  financialAssessmentSceneById,
} from '@/karehero-app/care/forms/FinancialAssessment.scenes';
import Onboarding from '@/karehero-app/onboarding/Onboarding';
import IntakeFlow from '@/karehero-app/tell-us-once/intake/Flow';
import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';
import CurrentOnboarding from '@/karehero-app/onboarding/current-flow/CurrentOnboarding';
import AccountLoadingScreen from '@/karehero-app/access/AccountLoadingScreen';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import WorkEmailScreen from '@/karehero-app/access/WorkEmailScreen';
import PartnerCodeScreen from '@/karehero-app/access/PartnerCodeScreen';
import PayrollIdScreen from '@/karehero-app/access/PayrollIdScreen';
import CodeScreen from '@/karehero-app/access/CodeScreen';
import { type Account } from '@/karehero-app/access/AccountBar';
import {
  InspectProvider,
  useInspect,
} from '@/designs-dev/inspect/InspectContext';
import InspectOverlay from '@/designs-dev/inspect/InspectOverlay';
import InspectPanel from '@/designs-dev/inspect/InspectPanel';
import LayersPanel from '@/designs-dev/inspect/LayersPanel';
import DrillModeToggle from '@/designs-dev/inspect/DrillModeToggle';
import StageBar, { STAGE_BAR_ROOM } from '@/designs-dev/inspect/StageBar';
import StateSelector from '@/designs-dev/inspect/StateSelector';
import { type Scene } from '@/designs-dev/scenes';

// Reads the work email handed over by the work email step (router state),
// so the code screen can name the inbox.
const InviteCodeRoute = ({
  account,
  onVerify,
  onUsePayrollId,
  onSignIn,
  onBack,
}: {
  account?: Account;
  onVerify: (code: string) => void;
  onUsePayrollId: () => void;
  onSignIn: () => void;
  onBack: () => void;
}) => {
  const { state } = useLocation();
  return (
    <CodeScreen
      email={state?.email}
      account={account}
      onVerify={onVerify}
      onUsePayrollId={onUsePayrollId}
      onSignIn={onSignIn}
      onBack={onBack}
    />
  );
};

// The centred stage: phone + drill-mode toggle, with the inspect panel docked
// beside it. Lives inside InspectProvider so it can react to drill mode.
const Stage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { active, current, setActive, setCurrent, setHovered } = useInspect();
  // Screens still refer to each other by the suffix they used to live at, since
  // that is what reads naturally in a handler: go('access'). The manifest turns
  // it into wherever that screen sits now.
  const go = (to: string) =>
    navigate(newPathFor(`/karehero-app/${to}`) ?? `/karehero-app/${to}`);

  // Shortlists V1 carries its own state switcher (open shortlist, provider
  // drawer, booking sheet, empty/error) — docked in the inspect panel and only
  // while that screen is the one showing. The scene set is the shared catalog.
  const onShortlists =
    location.pathname === newPathFor('/karehero-app/shortlists-v1');
  const [searchParams, setSearchParams] = useSearchParams();
  const [shortlistScene, setShortlistScene] = useState(
    () => searchParams.get('scene') ?? 'home',
  );
  const shortlistSceneId = shortlistScenes.some((s) => s.id === shortlistScene)
    ? shortlistScene
    : 'home';
  const { initial: shortlistInitial } = shortlistSceneById(shortlistSceneId);

  // Reflect the shortlist state in the URL so a state is a shareable address —
  // but only on that screen, so other member-app pages don't carry a ?scene.
  useEffect(() => {
    if (!onShortlists) return;
    if (searchParams.get('scene') !== shortlistSceneId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', shortlistSceneId);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onShortlists, shortlistSceneId]);

  // Switching state clears any pinned component, so the URL doesn't carry a
  // ?select from the old render into a screen where it no longer fits.
  const pickShortlistScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setShortlistScene(id);
  };

  // The intro carries two designs rather than two states: the switch in the
  // stage bar picks which opening is on screen. Same mechanism as the scened
  // screens below it, because a select on the stage is what Loft already has
  // for "which of these am I looking at".
  const onIntro = location.pathname === newPathFor('/karehero-app/intro');
  const [introScene, setIntroScene] = useState(
    () => searchParams.get('scene') ?? 'chat',
  );
  const introSceneId = introScenes.some((s) => s.id === introScene)
    ? introScene
    : 'chat';
  const introOption = introSceneById(introSceneId).initial;

  useEffect(() => {
    if (!onIntro) return;
    if (searchParams.get('scene') !== introSceneId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', introSceneId);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onIntro, introSceneId]);

  const pickIntroScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setIntroScene(id);
  };

  const pickFaScene = (id: string) => {
    setCurrent(null);
    setHovered(null);
    setFaScene(id);
  };

  // Financial assessment carries its own state switcher (intro, first question
  // with/without a name on file, review), on the same shared-catalog pattern as
  // Shortlists — docked in the inspect panel only while that screen shows.
  const onFinancialAssessment =
    location.pathname ===
    newPathFor('/karehero-app/forms/financial-assessment');
  const [faScene, setFaScene] = useState(
    () => searchParams.get('scene') ?? 'intro',
  );
  const faSceneId = financialAssessmentScenes.some((s) => s.id === faScene)
    ? faScene
    : 'intro';
  const { initial: faInitial } = financialAssessmentSceneById(faSceneId);

  useEffect(() => {
    if (!onFinancialAssessment) return;
    if (searchParams.get('scene') !== faSceneId) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scene', faSceneId);
          return next;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onFinancialAssessment, faSceneId]);

  // Phone mockup, full-bleed fill, or pinned to a breakpoint width.
  const [viewport, setViewport] = useState<Viewport>('frame');
  const framed = viewport === 'frame';
  // Screen element for drill mode when unframed (PhoneFrame provides its
  // own when framed).
  const [fullEl, setFullEl] = useState<HTMLElement | null>(null);

  const exitDrillMode = useCallback(() => {
    setActive(false);
    setCurrent(null);
    setHovered(null);
  }, [setActive, setCurrent, setHovered]);

  // Esc is staged: a first press clears a selected layer, a second (nothing
  // selected) leaves drill mode.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (current) {
        setCurrent(null);
        setHovered(null);
      } else {
        exitDrillMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, current, exitDrillMode, setCurrent, setHovered]);

  const welcome = (
    <WelcomeScreen
      onCreateAccount={() => go('signup')}
      onSignIn={() => go('signin')}
      onForgotPassword={() => go('forgot')}
    />
  );

  // Every member screen, keyed by the path it used to sit at. The manifest says
  // where each one lives now, so this holds the elements and nothing about URLs.
  // One mount owns the stage, so walking a flow keeps the drill toggle, the
  // viewport and the scene where they were; only the screen inside changes.
  const ELEMENTS: Record<string, ReactNode> = {
    // Before anything is asked for, and the way in as well: the actions at the
    // bottom go where the welcome screen's own did.
    '/karehero-app/intro': (
      <PreSignUp
        key={introSceneId}
        option={introOption}
        onCreateAccount={() => go('signup')}
        onSignIn={() => go('signin')}
      />
    ),
    '/karehero-app/welcome': welcome,
    '/karehero-app/signup': (
      <>
        {welcome}
        <KindeHostedSheet
          variant='register'
          onClose={() => go('welcome')}
          onComplete={() => go('creating-account')}
        />
      </>
    ),
    // logo-mark loader between set-password and the access flow
    '/karehero-app/creating-account': (
      <AccountLoadingScreen onContinue={() => go('access')} />
    ),
    '/karehero-app/access': (
      <FundingScreen
        onContinue={(choice) =>
          go(
            {
              work: 'verify-work-email',
              partner: 'partner-code',
              payroll: 'payroll-id',
            }[choice],
          )
        }
        onSignOut={() => go('welcome')}
      />
    ),
    '/karehero-app/verify-work-email': (
      <WorkEmailScreen
        account={{ onSignOut: () => go('welcome') }}
        onSubmit={(email) =>
          navigate(newPathFor('/karehero-app/invite-code') ?? '/', {
            state: { email },
          })
        }
        onBack={() => go('access')}
      />
    ),
    '/karehero-app/partner-code': (
      <PartnerCodeScreen
        account={{ onSignOut: () => go('welcome') }}
        onSubmit={() => go('verification-success')}
        onBack={() => go('access')}
      />
    ),
    '/karehero-app/payroll-id': (
      <PayrollIdScreen
        account={{ onSignOut: () => go('welcome') }}
        onSubmit={() => go('verification-success')}
        onBack={() => go('access')}
      />
    ),
    '/karehero-app/invite-code': (
      <InviteCodeRoute
        account={{ onSignOut: () => go('welcome') }}
        onVerify={() => go('verification-success')}
        onUsePayrollId={() => go('payroll-id')}
        // "Sign in instead": the fresh Kinde session is dropped and the sign-in
        // drawer reopens; an existing account skips onboarding and lands
        // straight in the app.
        onSignIn={() => go('signin')}
        onBack={() => go('verify-work-email')}
      />
    ),
    '/karehero-app/verification-success': (
      <AccessVerifiedScreen onContinue={() => go('intake')} />
    ),
    // Tell Us Once, carrying on from access rather than the older question
    // steps: the whole intake, walkable from the intro all the way through.
    //
    // Opened at the consent screen because the screen before it is the one you
    // just pressed Continue on: You're in is the intake's own first frame, and
    // the master draws it as the access flow's last.
    '/karehero-app/intake': <IntakeFlow initial='ai-consent' option={2} />,
    // Production-replica onboarding, kept for comparison. v1 of the project.
    '/karehero-app/onboarding-current': (
      <CurrentOnboarding onComplete={() => go('actions')} />
    ),
    '/karehero-app/signup-v2': (
      <SignUpFlow
        onExit={() => go('welcome')}
        onComplete={() => go('onboarding')}
      />
    ),
    // The designed onboarding (triage fork + steps 1-8), where the main flow
    // lands from Access verified.
    '/karehero-app/onboarding': (
      <Onboarding
        account={{ onSignOut: () => go('welcome') }}
        onComplete={() => go('home')}
        onExit={() => go('verification-success')}
      />
    ),
    '/karehero-app/signin': (
      <>
        {welcome}
        <KindeHostedSheet
          onClose={() => go('welcome')}
          onComplete={() => go('home')}
        />
      </>
    ),
    // Forgot password is Kinde's, the way sign-in and registration are: the
    // link on the welcome screen opens the hosted reset page in the same sheet,
    // and a reset signs them in, so it lands in the app rather than back here.
    '/karehero-app/forgot': (
      <>
        {welcome}
        <KindeHostedSheet
          variant='reset'
          onClose={() => go('welcome')}
          onComplete={() => go('home')}
        />
      </>
    ),
    // Shortlists V1: shortlists as a primary tab, home as a real page. key
    // remounts the prototype in the chosen state.
    '/karehero-app/shortlists-v1': (
      <ShortlistsV1Screen key={shortlistSceneId} initial={shortlistInitial} />
    ),
    // Care forms: financial assessment (one-question-per-screen flow).
    '/karehero-app/forms/financial-assessment': (
      <FinancialAssessment
        key={faSceneId}
        onComplete={() => go('home')}
        onExit={() => go('home')}
        {...faInitial}
      />
    ),
    // The tabs. Home, chat, settings and notifications are the master
    // prototype's; actions is a project of its own.
    '/karehero-app/home': <MainTabs tab='home' />,
    '/karehero-app/actions': <MainTabs tab='actions' />,
    '/karehero-app/profile': <MainTabs tab='profile' />,
    '/karehero-app/care': <MainTabs tab='care' />,
    '/karehero-app/notifications': <MainTabs tab='notifications' />,
    '/karehero-app/chat': <MainTabs tab='chat' />,
    '/karehero-app/settings': <MainTabs tab='settings' />,
  };

  // Routes are relative to the /master mount, so a screen's slug in the manifest
  // is its path here. The manifest holds the structure, this holds the elements,
  // and the two meet on `legacy`.
  const masterProject = master();
  const masterVersion = sourceOfTruth(masterProject);
  const screens = masterVersion.screens;

  // Getting in is fourteen screens wired to each other, and clicking from the
  // welcome screen to the payroll id one to look at it is four taps and a
  // guess. So it carries a screen selector, the way Tell Us Once's prototype
  // does, and picking a screen navigates to it.
  //
  // Derived from the manifest rather than listed again: a screen added there
  // appears here, at the path the manifest says it lives at.
  const onboardingScenes: Scene<string>[] = screens
    .filter((sc) => sc.slug.startsWith('onboarding/'))
    .map((sc) => ({
      id: sc.slug,
      label: sc.label,
      kind: 'happy' as const,
      section: ONBOARDING_SECTION[sc.slug] ?? 'signing-up',
      initial: screenPath(masterProject, masterVersion, sc),
    }));
  const openOnboarding = onboardingScenes.find(
    (sc) => sc.initial === location.pathname,
  );

  const screen = (
    <>
      <Routes>
        {/* The front door, so /master lands where a member would start. */}
        <Route index element={<Navigate to='onboarding/intro' replace />} />
        {screens.map((s) => {
          const element = s.legacy ? ELEMENTS[s.legacy] : undefined;
          return element ? (
            <Route key={s.slug} path={s.slug} element={element} />
          ) : null;
        })}
        <Route path='*' element={<Navigate to='home' replace />} />
      </Routes>
      <InspectOverlay />
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: active ? 'custom.charcoal' : 'custom.neutral.100',
        transition: 'background-color 400ms ease',
      }}
    >
      {/* The stage, with the controls floating over its foot. */}
      <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            display: 'flex',
            alignItems: framed ? 'center' : 'stretch',
            justifyContent: 'center',
            p: 4,
            // Room for the floating controls under the screen.
            pb: STAGE_BAR_ROOM,
            overflow: 'auto',
          }}
        >
          {/* Exit, top right: only while drill mode is on, to leave it (Esc
            works too). In plain view mode there's nothing to exit. */}
          {active && (
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
              <Stack
                direction='row'
                spacing={1}
                onClick={exitDrillMode}
                sx={{
                  alignItems: 'center',
                  px: 2,
                  py: 1,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'custom.neutral.200',
                  borderRadius: `${radius.md}px`,
                  cursor: 'pointer',
                }}
              >
                <Icon
                  icon={'arrow-right-from-bracket' as never}
                  fontSize={16}
                />
                <Typography variant='subtitle2'>Exit drill mode</Typography>
              </Stack>
            </Box>
          )}

          <Stack
            sx={{
              alignItems: 'center',
              width: framed ? 'auto' : viewport === 'fill' ? '100%' : viewport,
              flex: viewport === 'fill' ? 1 : undefined,
              minHeight: 0,
            }}
          >
            {framed ? (
              <PhoneFrame inspecting={active}>{screen}</PhoneFrame>
            ) : (
              /* out of the frame: fills the stage — e.g. opening designs.dev
               on a real phone for user testing */
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: `${radius.md}px`,
                  border: 1,
                  borderColor: 'custom.neutral.200',
                  bgcolor: 'background.default',
                }}
                ref={(el: HTMLElement | null) => setFullEl(el)}
              >
                <PhoneScreenContext.Provider value={fullEl}>
                  {screen}
                </PhoneScreenContext.Provider>
              </Box>
            )}
          </Stack>
        </Box>

        {/* How you are looking at the design, then what it is showing.
            Whichever of the scened screens is open supplies the state
            catalog; on every other screen there is no state to pick, so only
            the drill toggle is there. */}
        <StageBar>
          <DrillModeToggle viewport={viewport} onViewportChange={setViewport} />
          {onIntro && (
            <StateSelector
              scenes={introScenes}
              sceneId={introSceneId}
              onSceneChange={pickIntroScene}
            />
          )}
          {onShortlists && (
            <StateSelector
              scenes={shortlistScenes}
              sceneId={shortlistSceneId}
              onSceneChange={pickShortlistScene}
            />
          )}
          {onFinancialAssessment && (
            <StateSelector
              scenes={financialAssessmentScenes}
              sceneId={faSceneId}
              onSceneChange={pickFaScene}
            />
          )}
          {openOnboarding && (
            <StateSelector
              scenes={onboardingScenes}
              sceneId={openOnboarding.id}
              onSceneChange={(id) => {
                setCurrent(null);
                setHovered(null);
                const to = onboardingScenes.find((sc) => sc.id === id);
                if (to) navigate(to.initial);
              }}
              sections={ONBOARDING_SECTIONS}
            />
          )}
        </StageBar>
      </Box>

      {/* Docked full-height side panel (like the designs.dev menu), in flow
          beside the stage. Renders null when inspect mode is off. */}
      <LayersPanel />
      <InspectPanel />
    </Box>
  );
};

// KareHero App (member-facing) prototype. Screens are real routes under
// /karehero-app/* (welcome, signup, signin, forgot, home, actions, profile,
// care, chat)
// so you can deep-link instead of clicking through.
// The parts of getting in, for the screen selector. Fourteen screens in one
// list is a wall; these are the four things that are actually going on in it.
//
// A map rather than something derived from the slugs: 'access' and
// 'verify-work-email' are both access checks and nothing in the names says so.
const ONBOARDING_SECTIONS = [
  { id: 'signing-up', label: 'Signing up' },
  { id: 'access', label: 'Proving access' },
  { id: 'questions', label: 'The questions after' },
  { id: 'older', label: 'What it replaces' },
];

const ONBOARDING_SECTION: Record<string, string> = {
  'onboarding/intro': 'signing-up',
  'onboarding/welcome': 'signing-up',
  'onboarding/signup': 'signing-up',
  'onboarding/signin': 'signing-up',
  'onboarding/forgot': 'signing-up',
  'onboarding/creating-account': 'signing-up',
  'onboarding/verify-work-email': 'access',
  'onboarding/verification-success': 'access',
  'onboarding/intake': 'questions',
  'onboarding/invite-code': 'access',
  'onboarding/partner-code': 'access',
  'onboarding/payroll-id': 'access',
  'onboarding/access': 'access',
  'onboarding/questions': 'questions',
  'onboarding/current': 'older',
  'onboarding/current-signup': 'older',
};

const KareHeroApp = () => (
  <InspectProvider>
    <Stage />
  </InspectProvider>
);

export default KareHeroApp;
